import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

export const EscalationModal = ({ deadline, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    escalationType: 'formal_notice',
    urgency: 'high',
    recipientMinistry: '',
    recipientOfficer: '',
    ccAuthorities: [],
    legalAction: false,
    notes: '',
    attachEvidence: true,
    requestMeeting: false,
    meetingDate: ''
  });

  const escalationTypes = [
    { 
      id: 'formal_notice', 
      label: 'Formal Notice', 
      description: 'Official written notice of non-compliance',
      icon: FiIcons.FiFileText 
    },
    { 
      id: 'ministerial_alert', 
      label: 'Ministerial Alert', 
      description: 'Direct notification to Minister/Permanent Secretary',
      icon: FiIcons.FiAlertTriangle 
    },
    { 
      id: 'ppu_referral', 
      label: 'PPU Referral', 
      description: 'Refer to Public Procurement Unit for investigation',
      icon: FiIcons.FiShield 
    },
    { 
      id: 'legal_action', 
      label: 'Legal Action', 
      description: 'Initiate legal proceedings for breach',
      icon: FiIcons.FiAlertOctagon 
    }
  ];

  const authorities = [
    { id: 'ppu', label: 'Public Procurement Unit (PPU)' },
    { id: 'ag', label: 'Auditor General' },
    { id: 'acc', label: 'Anti-Corruption Commission' },
    { id: 'parliament', label: 'Parliamentary Committee' },
    { id: 'treasury', label: 'Ministry of Finance' }
  ];

  const handleCCToggle = (authorityId) => {
    setFormData(prev => ({
      ...prev,
      ccAuthorities: prev.ccAuthorities.includes(authorityId)
        ? prev.ccAuthorities.filter(id => id !== authorityId)
        : [...prev.ccAuthorities, authorityId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.recipientMinistry || !formData.recipientOfficer) {
      alert('Please specify recipient ministry and officer');
      return;
    }

    const escalation = {
      deadlineId: deadline.id,
      type: formData.escalationType,
      urgency: formData.urgency,
      recipient: {
        ministry: formData.recipientMinistry,
        officer: formData.recipientOfficer
      },
      ccAuthorities: formData.ccAuthorities,
      legalAction: formData.legalAction,
      notes: formData.notes,
      attachEvidence: formData.attachEvidence,
      requestMeeting: formData.requestMeeting,
      meetingDate: formData.meetingDate,
      timestamp: new Date().toISOString(),
      initiatedBy: 'Prime Minister\'s Office',
      status: 'pending'
    };

    onSubmit(escalation);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-red-200 bg-red-50 flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900">Escalate Deadline Breach</h2>
              <p className="text-sm text-red-700 mt-1">{deadline.title}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                  {deadline.isOverdue ? 'BREACH' : 'CRITICAL'}
                </span>
                <span className="text-xs text-red-600 font-mono">
                  {deadline.legalBasis}
                </span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiX} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Escalation Type */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
              Escalation Type *
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {escalationTypes.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, escalationType: type.id })}
                    className={clsx(
                      "p-4 rounded-lg border-2 text-left transition-all",
                      formData.escalationType === type.id
                        ? "border-red-500 bg-red-50"
                        : "border-slate-200 hover:border-red-200"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={clsx(
                        "p-2 rounded-lg",
                        formData.escalationType === type.id
                          ? "bg-red-100 text-red-600"
                          : "bg-slate-100 text-slate-500"
                      )}>
                        <SafeIcon icon={Icon} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800 text-sm mb-1">
                          {type.label}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {type.description}
                        </p>
                      </div>
                      {formData.escalationType === type.id && (
                        <SafeIcon 
                          icon={FiIcons.FiCheckCircle} 
                          className="text-red-600 flex-shrink-0" 
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Urgency Level */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
              Urgency Level *
            </h3>
            <div className="flex gap-3">
              {[
                { id: 'high', label: 'High', color: 'red' },
                { id: 'critical', label: 'Critical', color: 'red' },
                { id: 'emergency', label: 'Emergency', color: 'red' }
              ].map(level => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, urgency: level.id })}
                  className={clsx(
                    "flex-1 px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all",
                    formData.urgency === level.id
                      ? `border-${level.color}-500 bg-${level.color}-50 text-${level.color}-700`
                      : "border-slate-200 text-slate-600 hover:border-slate-300"
                  )}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recipient Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Recipient Ministry *
              </label>
              <select
                value={formData.recipientMinistry}
                onChange={(e) => setFormData({ ...formData, recipientMinistry: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
                required
              >
                <option value="">Select Ministry</option>
                <option value="MWT">Ministry of Works & Transport</option>
                <option value="MOE">Ministry of Education</option>
                <option value="MOHSS">Ministry of Health</option>
                <option value="MAF">Ministry of Agriculture</option>
                <option value="MME">Ministry of Mines & Energy</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Recipient Officer *
              </label>
              <input
                type="text"
                value={formData.recipientOfficer}
                onChange={(e) => setFormData({ ...formData, recipientOfficer: e.target.value })}
                placeholder="e.g., Permanent Secretary"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
                required
              />
            </div>
          </div>

          {/* CC Authorities */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
              CC Oversight Authorities
            </h3>
            <div className="space-y-2">
              {authorities.map(auth => (
                <label
                  key={auth.id}
                  className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.ccAuthorities.includes(auth.id)}
                    onChange={() => handleCCToggle(auth.id)}
                    className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500/20"
                  />
                  <span className="text-sm text-slate-700">{auth.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-4 border border-amber-200 bg-amber-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={formData.legalAction}
                onChange={(e) => setFormData({ ...formData, legalAction: e.target.checked })}
                className="w-4 h-4 text-amber-600 rounded focus:ring-2 focus:ring-amber-500/20 mt-0.5"
              />
              <div>
                <span className="text-sm font-bold text-amber-900">Recommend Legal Action</span>
                <p className="text-xs text-amber-700 mt-1">
                  Flag this case for potential legal proceedings under the Public Procurement Act
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={formData.attachEvidence}
                onChange={(e) => setFormData({ ...formData, attachEvidence: e.target.checked })}
                className="w-4 h-4 text-grn-navy rounded focus:ring-2 focus:ring-grn-navy/20 mt-0.5"
              />
              <div>
                <span className="text-sm font-bold text-slate-800">Attach Supporting Evidence</span>
                <p className="text-xs text-slate-600 mt-1">
                  Include timeline, communications, and project documentation
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={formData.requestMeeting}
                onChange={(e) => setFormData({ ...formData, requestMeeting: e.target.checked })}
                className="w-4 h-4 text-grn-navy rounded focus:ring-2 focus:ring-grn-navy/20 mt-0.5"
              />
              <div className="flex-1">
                <span className="text-sm font-bold text-slate-800">Request Emergency Meeting</span>
                <p className="text-xs text-slate-600 mt-1 mb-2">
                  Schedule urgent meeting with responsible parties
                </p>
                {formData.requestMeeting && (
                  <input
                    type="datetime-local"
                    value={formData.meetingDate}
                    onChange={(e) => setFormData({ ...formData, meetingDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
                  />
                )}
              </div>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Escalation Notes & Justification
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Provide context, impact assessment, and recommended actions..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
              rows={4}
            />
          </div>

          {/* Legal Warning */}
          <div className="bg-slate-900 text-white rounded-lg p-4 flex gap-3">
            <SafeIcon icon={FiIcons.FiInfo} className="text-lg flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-bold mb-1">Legal Notice</p>
              <p className="opacity-90">
                This escalation will create an official record in the National Compliance System. 
                All communications will be logged and may be subject to parliamentary oversight 
                and audit review. False or malicious escalations may result in disciplinary action.
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="text-xs text-slate-500">
            Escalation will be logged at {new Date().toLocaleString('en-GB')}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2 transition-colors"
            >
              <SafeIcon icon={FiIcons.FiSend} />
              Submit Escalation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};